<template>
  <v-dialog v-model="open" max-width="760" scrollable>
    <v-card class="kf-perf">
      <v-card-title class="kf-perf__title">
        <LucideIcon name="chart-column" :size="20" class="mr-2" />
        {{ campaign ? campaign.name : "Performance" }}
        <v-spacer />
        <DateField
          v-model="range"
          range
          :max="today"
          label="All time"
          class="kf-perf__range"
          @change="load"
        />
      </v-card-title>

      <v-card-text class="kf-perf__body">
        <ErrorState v-if="error" :error="error" @retry="load" />
        <v-skeleton-loader v-else-if="loading" type="table-tbody" />

        <template v-else-if="data">
          <div class="kf-perf__tiles">
            <div class="kf-perf__tile">
              <div class="kf-perf__n">{{ data.totals.clicks }}</div>
              <div class="kf-perf__k">Clicks</div>
            </div>
            <div class="kf-perf__tile">
              <div class="kf-perf__n">{{ data.offer_clicks.total }}</div>
              <div class="kf-perf__k">Offer clicks</div>
            </div>
            <div class="kf-perf__tile">
              <div class="kf-perf__n">{{ data.totals.signups }}</div>
              <div class="kf-perf__k">Signups</div>
            </div>
          </div>

          <div
            v-if="!data.rows.length && !data.offer_clicks.products.length"
            class="kf-perf__empty"
          >
            Nothing yet for this campaign{{ range.length === 2 ? " in these dates" : "" }}.
          </div>
          <v-simple-table v-else-if="data.rows.length" dense>
            <thead>
              <tr>
                <th>Partner ID</th>
                <th>Publisher</th>
                <th>Source</th>
                <th class="text-end">Clicks</th>
                <th class="text-end">Signups</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in data.rows"
                :key="[row.partner_id, row.publisher, row.source, i].join('|')"
              >
                <td>{{ row.partner_id || "—" }}</td>
                <td>{{ row.publisher || "—" }}</td>
                <td>{{ row.source || "—" }}</td>
                <td class="text-end">{{ row.clicks }}</td>
                <td class="text-end">{{ row.signups }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <!-- Per product, not per partner: a tapped offer's link carries the
               campaign but not which partner's link the visitor came in on. -->
          <template v-if="data.offer_clicks.products.length">
            <div class="kf-perf__sub">Offer clicks by product</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Product</th>
                  <th class="text-end">Offer clicks</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in data.offer_clicks.products" :key="row.product_id || 'none'">
                  <td>{{ row.name || "—" }}</td>
                  <td class="text-end">{{ row.clicks }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>

          <div class="kf-perf__note">
            A click is one visitor through the partner link (repeats within a minute
            count once). An offer click is an offer tapped on the campaign page,
            sending the visitor to the lender. A signup is a customer whose first
            source was this campaign — through the app or the website. “—” means
            the partner left that value out of the link.
          </div>
        </template>
      </v-card-text>

      <v-card-actions class="kf-perf__actions">
        <v-spacer />
        <v-btn text small class="text-none" @click="open = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * One campaign's partner link, partner by partner, and the offers tapped on
 * its page, product by product.
 *
 * Rows are partner_id / publisher / source as the partner put them in the link
 * -- the same values that land on a customer's utm_source / utm_medium /
 * utm_content -- so a row here is what the UTM filters on the customer screens
 * will find for that partner.
 */
import DateField from "@/components/karrofin/DateField.vue";
import ErrorState from "@/components/karrofin/ErrorState.vue";
import api from "@/app-js/karrofin/api";

export default {
  name: "KfCampaignPerformanceDialog",
  components: { DateField, ErrorState },
  props: {
    value: { type: Boolean, default: false },
    campaign: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      error: null,
      data: null,
      // [from, to] as YYYY-MM-DD, or [] for all time.
      range: [],
      today: new Date().toISOString().substr(0, 10),
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.data = null;
        this.range = [];
        this.load();
      }
    },
  },
  methods: {
    load() {
      if (!this.campaign) return;
      this.loading = true;
      this.error = null;
      const params =
        this.range.length === 2 ? { date_from: this.range[0], date_to: this.range[1] } : {};
      Promise.resolve()
        .then(() => api.getCampaignStats(this.campaign.id, params))
        .then((data) => {
          this.data = data;
        })
        .catch((err) => {
          this.error = err;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.kf-perf__title {
  font-size: var(--kf-fs-title);
  font-weight: 700;
  padding-bottom: 8px;
}
.kf-perf__range {
  max-width: 240px;
  flex: 0 0 auto;
}
.kf-perf__body {
  max-height: 64vh;
}
.kf-perf__tiles {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}
.kf-perf__tile {
  padding: 10px 12px;
  border: 1px solid #EEE;
  border-radius: 6px;
}
.kf-perf__n {
  font-size: 20px;
  font-weight: 700;
  color: #000;
}
.kf-perf__k {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
}
.kf-perf__sub {
  margin: 16px 0 4px;
  font-size: var(--kf-fs-label);
  font-weight: 600;
}
.kf-perf__empty,
.kf-perf__note {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
}
.kf-perf__empty {
  padding: 18px 0;
  text-align: center;
}
.kf-perf__note {
  margin-top: 12px;
  line-height: 1.5;
}
.kf-perf__actions {
  padding: 8px 16px 14px;
}
</style>
